import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../login/logincss/Login.css";
import logo from "../../images/auravit_logo.png";

// NotificationModal component
function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      className={`feedback-modal ${type}`}
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        padding: "12px 20px",
        borderRadius: "8px",
        color: "#fff",
        backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
        zIndex: 9999,
        transition: "opacity 0.3s",
      }}
    >
      {message}
    </div>
  );
}

export default function UserVerification() {
  const location = useLocation();
  const navigate = useNavigate();
  const verificationType = location.state?.type || "login";
  const newEmail = location.state?.newEmail;
  const emailDisplay = location.state?.otpEmail || location.state?.email || "your registered email";
  const userRole = location.state?.role || "user"; // Can be 'user', 'admin', or 'staff'

  // OTP Configuration: 6 digits
  const OTP_LENGTH = 6;

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // OTP expires in 60 seconds
  const [resendText, setResendText] = useState("Resend");
  const [otpExpired, setOtpExpired] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const inputsRef = useRef([]);

  // Animation state based on timer
  const getTimerState = () => {
    if (otpExpired) return "expired";
    if (timeLeft <= 5) return "critical";
    if (timeLeft <= 15) return "warning";
    return "normal";
  };

  const getCardClasses = () => {
    const baseClass = "forgot-card";
    const timerState = getTimerState();
    
    if (timerState === "expired") return `${baseClass} timer-expired`;
    if (timerState === "critical") return `${baseClass} timer-critical timer-active`;
    if (timeLeft > 0) return `${baseClass} timer-active`;
    return baseClass;
  };

  const getTimerClasses = () => {
    const timerState = getTimerState();
    return `timer timer-${timerState}`;
  };

  // Countdown timer logic - OTP expires after 60 seconds
  useEffect(() => {
    if (timeLeft <= 0) {
      setOtpExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Reset expiration when user requests new OTP
  const resetOtpExpiration = () => {
    setOtpExpired(false);
    setTimeLeft(60);
  };

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Validate OTP is complete (6 digits)
  const isOtpComplete = otp.every((digit) => digit !== "");

  // Handle typing - digits only, auto-focus next box
  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(""); // Clear error when user starts typing

    // Automatically move focus to the next box
    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  // Handle backspace - move to previous box
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Handle pasting a 6-digit code
  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim().slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      const newOtp = Array(OTP_LENGTH).fill("");
      pasteArray.forEach((char, i) => {
        if (i < OTP_LENGTH) newOtp[i] = char;
      });

      setOtp(newOtp);
      setError("");
      // Focus the last filled box
      const focusIndex = Math.min(pasteArray.length, OTP_LENGTH - 1);
      inputsRef.current[focusIndex].focus();
    }
    e.preventDefault();
  };

  // Handle OTP verification - UPDATED FOR MULTIPLE FLOWS
  const handleVerify = async () => {
    // Validate OTP is complete
    if (!isOtpComplete) {
      setError("Please enter the full 6-digit code");
      // Reset OTP boxes when incomplete
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    // Check if OTP has expired
    if (otpExpired) {
      setError("Code has expired. Please request a new code.");
      // Reset OTP boxes when expired
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    // In Firebase-only mode we no longer have a backend OTP;
    // as long as the UI code is complete, continue based on the flow type.
    setIsVerifying(true);
    try {
      if (verificationType === "registration") {
        // Registration: user was already created in Firebase Auth in `AccountInformation`.
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        navigate("/user-home", { state: { user: stored, fromSignup: true } });
      } else if (verificationType === "login") {
        // Login: user is already logged-in with Firebase Auth in `Login.jsx`.
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        navigate("/user-home", { state: { user: stored } });
        sessionStorage.removeItem('freshLoginInProgress');
      } else if (verificationType === "email-change-step1" || verificationType === "email-change") {
        // Email change is now handled by Firebase Auth flows; just return to profile.
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        if (userRole === "admin") {
          navigate("/admin-management", { state: { user: stored } });
        } else {
          navigate("/user-profile", { state: { user: stored } });
        }
      } else if (verificationType === "password-change") {
        // Password change is now handled directly via Firebase Auth re-auth + updatePassword.
        setNotification({ message: "Password updated.", type: "success" });
        setTimeout(() => {
          if (userRole === "admin") {
            navigate("/admin-management");
          } else {
            navigate("/user-home");
          }
        }, 600);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle resend OTP - UPDATED TO USE RESEND ENDPOINT
  const handleResend = async () => {
    // No backend OTP to resend; just reset the UI timer and inputs.
    if (resendText === "Sent!") return;
    setResendText("Sent!");
    setError("");
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpExpired(false);
    setTimeLeft(60);
    inputsRef.current[0]?.focus();
    setTimeout(() => setResendText("Resend"), 2000);
  };

  return (
    <>
    <div className="verification-logo-container">
      <img
        src={logo}
        alt="AuraVit"
        className="verification-logo"
        onClick={() => navigate("/")}
      />
    </div>
    <div className="verification-container">
      <div className="verification-card">
        <h1 className="verification-title">Enter Verification Code</h1>
        <p className="verification-subtitle">
          Enter 6-digit code we sent to: <strong>{emailDisplay}</strong>
        </p>

        <div className={`verification-otp-inputs ${error ? "shake" : ""}`}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className={`verification-otp-input ${error && !digit ? "error" : ""}`}
              disabled={isVerifying}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && <div className="verification-error-text">{error}</div>}

        {otpExpired && (
          <div className="verification-error-text">Code has expired. Please request a new one.</div>
        )}

        <p className="verification-timer-section">
          Code expires in <span className={`verification-timer ${otpExpired ? "expired" : ""}`}>{formatTime(timeLeft)}</span>
        </p>

        <p className="verification-resend-section">
          Didn't receive a code?{" "}
          <span className={`verification-resend-link ${resendText === "Sent!" ? "disabled" : ""}`} onClick={handleResend} style={{ pointerEvents: resendText === "Sent!" ? "none" : "auto", opacity: resendText === "Sent!" ? 0.5 : 1 }}>{resendText}</span>
        </p>

        <button className="verification-verify-btn" onClick={handleVerify} disabled={isVerifying || !isOtpComplete || otpExpired}>
          {isVerifying ? "Verifying..." : "Verify"}
        </button>

        <span className="back-link" onClick={() => {
          if (verificationType === "registration") {
            navigate("/");
          } else if (verificationType === "login") {
            navigate("/login");
          } else {
            if (userRole === "admin") {
              navigate("/admin-management");
            } else {
              navigate("/user-profile");
            }
          }
        }}>
          &lt; Back
        </span>
      </div>
    </div>
      <NotificationModal
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
    </>
  );
}
